/** @jsxImportSource @emotion/react */
import React from "react";
import {HexColorPicker} from "react-colorful";
import {Button, Checkbox, FormControlLabel, IconButton, MenuItem, Radio, RadioGroup, Select, Slider, Switch, Tooltip} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faInfoCircle, faCloudUploadAlt} from "@fortawesome/pro-regular-svg-icons";

import Drawer from "js/common/views/drawer";
import Cube19Dialog from "js/common/views/dialog";
import Dropzone from "js/common/views/inputs/dropzone";
import FormField from "js/common/views/inputs/form-field";
import DelayedTextField from "js/common/views/inputs/simple-text-input";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import {GAMIFIED_ASPECT_RATIOS} from "js/dashboards/utils";
import {AspectRatioCard} from "js/dashboards/sidebar/create-dashboard-selector";
import {DEFAULT_APPEARANCE, DEFAULT_ZOOM, getMatchedPanelColor, validateBackgroundImageFile, ZOOM_MIN, ZOOM_MAX, ZOOM_STEP} from "js/dashboards/settings/appearance-utils";
import {uploadDashboardBackgroundImage} from "js/dashboards/settings/upload-dashboard-background";

const RIGHTS_COPY = "Bullhorn Analytics service permits users to upload a background image for a Dashboards, " +
  "nothing herein constitutes an authorization by Bullhorn regarding your use of any specific image. " +
  "By uploading, you are representing that you have the necessary rights to use the image uploaded.";

const DEFAULT_GRADIENT = {from: "#1e1e2e", to: "#3a3a5e", angle: 135};

const sectionStyle = {padding: "16px", borderBottom: "1px solid rgba(128,128,128,0.2)"};
const labelStyle = {fontSize: 13, fontWeight: 600, marginBottom: 8, display: "flex", alignItems: "center", gap: 6};
// Body shown below the horizontal background-type radios once a type is selected.
const optionBodyStyle = {marginTop: 12, marginBottom: 4};
const pickerStyle = {width: "100%", height: 130};

// Accepts #rgb or #rrggbb (case-insensitive). Used to guard the hex text inputs so a
// malformed value is flagged rather than silently fed to the colour picker.
const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const isValidHex = (v) => HEX_RE.test((v || "").trim());

// A colour picker paired with a hex text field that validates its input. The picker
// only ever receives the last committed (valid) colour; typing an invalid hex shows an
// inline warning and leaves the committed colour untouched rather than jumping to a
// random colour. Defined at module scope so its local text state survives parent
// re-renders (an inline component would remount on every draft change).
const HexField = ({label, value, placeholder, testId, theme, onChange}) => {
  const [text, setText] = React.useState(value);
  React.useEffect(() => { setText(value); }, [value]);
  const invalid = !!text && !isValidHex(text);
  return (
    <FormField label={label}>
      <HexColorPicker style={pickerStyle} color={value} onChange={onChange} />
      <div style={{marginTop: 8}}>
        <DelayedTextField value={text}
                          placeholder={placeholder}
                          data-test-id={testId}
                          onChange={(v) => {
                            setText(v);
                            if (isValidHex(v)) {
                              onChange(v.trim());
                            }
                          }} />
        {invalid && (
          <div data-test-id={`${testId}-error`}
               style={{color: theme.palette.error.main, fontSize: 11, marginTop: 4}}>
            Enter a valid hex colour, e.g. {placeholder}
          </div>
        )}
      </div>
    </FormField>
  );
};

const dropzoneInnerStyle = (theme) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  padding: "20px 12px",
  border: `1px dashed ${theme.palette.border.main || theme.palette.border.light}`,
  borderRadius: 6,
  cursor: "pointer"
});

const DashboardSettingsDrawer = ({open, appearance, aspectRatio, isGamifiedDashboard, onApply, onClose, onPreview = () => {}}) => {
  const {theme} = React.useContext(CustomThemeContext);
  // Merge over the defaults so a dashboard with no (or partial) saved appearance still
  // gets a selected background type, colour etc. — an empty {} would otherwise leave
  // the background radio group with nothing selected.
  const withDefaults = (a) => ({...DEFAULT_APPEARANCE, ...(a || {})});
  const [draft, setDraft] = React.useState(withDefaults(appearance));
  const [draftAspectRatio, setDraftAspectRatio] = React.useState(aspectRatio || "16:9");
  const [pendingAspectRatio, setPendingAspectRatio] = React.useState(null);
  const [uploadError, setUploadError] = React.useState(null);
  const [isUploading, setIsUploading] = React.useState(false);

  const wasOpenRef = React.useRef(false);

  React.useEffect(() => {
    if (open && !wasOpenRef.current) {
      setDraft(withDefaults(appearance));
      setDraftAspectRatio(aspectRatio || "16:9");
      setUploadError(null);
    }
    wasOpenRef.current = open;
  }, [open, appearance, aspectRatio]);

  // Live preview: while the drawer is open, push the draft appearance up so the
  // dashboard renders it without persisting. The parent reverts on cancel/close.
  React.useEffect(() => {
    if (open) {
      onPreview(draft);
    }
  }, [draft, open, onPreview]);

  const update = (patch) => setDraft(prev => ({...prev, ...patch}));

  // The background colour doubles as the gradient's start colour, so when a gradient is
  // active keep its `from` in sync — otherwise editing the start colour would only move
  // the picker while the gradient kept rendering the colour it was seeded with.
  const updateBackgroundColor = (c) => setDraft(prev => ({
    ...prev,
    backgroundColor: c,
    ...(prev.backgroundGradient ? {backgroundGradient: {...prev.backgroundGradient, from: c}} : {})
  }));

  // The colour shown in the background picker: fall back to the theme's background
  // colour when none is set, so the picker never receives null.
  const backgroundColorValue = draft.backgroundColor || theme.palette.background.paper;

  // The panel colour shown in the picker: while matching, it tracks the background;
  // otherwise it's the explicitly picked colour, defaulting to the matched colour.
  // Fall back to the theme's panel/card colour when nothing can be derived (e.g. an
  // image background) so the colour picker never receives null.
  const matchedPanelColor = getMatchedPanelColor(draft) || theme.palette.background.card;
  const panelColorValue = (draft.panelMatch ? matchedPanelColor : draft.panelColor) || matchedPanelColor;

  const handleDrop = (files) => {
    const file = files[0];
    if (!file) {
      return;
    }
    const {valid, error} = validateBackgroundImageFile(file);
    if (!valid) {
      setUploadError(error);
      return;
    }
    setUploadError(null);
    setIsUploading(true);
    uploadDashboardBackgroundImage(file)
      .then(url => setDraft(prev => ({
        ...prev,
        backgroundType: "image",
        backgroundImage: {url, fit: (prev.backgroundImage && prev.backgroundImage.fit) || "cover"}
      })))
      .catch(() => setUploadError("Upload failed. Please try again."))
      .finally(() => setIsUploading(false));
  };

  // Keep the Image option selected so a new image can be chosen without re-picking
  // the radio; the dashboard falls back to the solid colour until one is uploaded.
  const removeImage = () => update({backgroundImage: null});

  const handleAspectRatioSelect = (value) => {
    if (value !== draftAspectRatio) {
      setPendingAspectRatio(value);
    }
  };

  // The drawer must only be dismissed via the close/cancel buttons, so ignore
  // MUI's backdrop-click and escape-key close reasons.
  const handleRequestClose = (event, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
    onClose();
  };

  return (
    <Drawer open={open} onRequestClose={handleRequestClose} openRight={true} width={500}
            showCloseButton={true} title="Dashboard Settings"
            dataTestId="dashboard-settings-drawer">
      <div style={{display: "flex", flexDirection: "column", height: "calc(100% - 64px)"}}>
        <div style={{flex: 1, overflowY: "auto"}}>

          {/* These settings live on the dashboard, not the tab, and the drawer is opened
              from a single tab — so say so before anything is changed. */}
          <div data-test-id="dashboard-settings-scope-note"
               style={{padding: "12px 16px 4px", fontSize: 12, color: theme.palette.text.secondary}}>
            Changes will update settings across all tabs.
          </div>

          <div style={sectionStyle}>
            <div style={labelStyle}>Page Background</div>
            {/* Radios laid out horizontally so selecting Image doesn't shove the Image
                option down the page (the option bodies render below, not inline). */}
            <RadioGroup row value={draft.backgroundType}
                        onChange={(e) => update({backgroundType: e.target.value})}>
              <FormControlLabel value="color" label="Color"
                                control={<Radio size="small" inputProps={{"data-test-id": "background-mode-color"}} />} />
              <FormControlLabel value="image"
                                control={<Radio size="small" inputProps={{"data-test-id": "background-mode-image"}} />}
                                label={
                                  <span style={{display: "flex", alignItems: "center", gap: 6}}>
                                    Image
                                    <Tooltip title={RIGHTS_COPY} arrow>
                                      <span data-test-id="background-image-rights-info" style={{display: "inline-flex"}}>
                                        <FontAwesomeIcon icon={faInfoCircle} style={{color: theme.palette.text.secondary}} />
                                      </span>
                                    </Tooltip>
                                  </span>
                                } />
            </RadioGroup>

            {draft.backgroundType === "color" && (
              <div style={optionBodyStyle}>
                {/* Gradient toggle sits first, then the start colour, then the end
                    colour, so the controls read top-to-bottom in the order they apply. */}
                <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12}}>
                  <span style={{fontSize: 13, fontWeight: 500}}>Gradient</span>
                  <Switch inputProps={{"data-test-id": "gradient-toggle"}}
                          checked={!!draft.backgroundGradient}
                          onChange={(e) => update({
                            backgroundGradient: e.target.checked
                              ? {...DEFAULT_GRADIENT, from: backgroundColorValue}
                              : null
                          })} />
                </div>

                <div style={{position: "relative"}}>
                  <HexField label={draft.backgroundGradient ? "Gradient start color" : "Background color"}
                            value={backgroundColorValue}
                            placeholder="#1e1e2e"
                            testId="background-hex-input"
                            theme={theme}
                            onChange={updateBackgroundColor} />
                  {(draft.backgroundColor || draft.backgroundGradient) && (
                    <Tooltip title="Reset to theme default">
                      <IconButton
                          size="small"
                          data-test-id="background-color-clear"
                          onClick={() => update({backgroundColor: null, backgroundGradient: null})}
                          style={{position: "absolute", top: 0, right: 0}}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>

                {draft.backgroundGradient && (
                  <div style={{marginTop: 12}}>
                    <HexField label="Gradient end color"
                              value={draft.backgroundGradient.to}
                              placeholder="#3a3a5e"
                              testId="gradient-end-hex-input"
                              theme={theme}
                              onChange={(c) => update({backgroundGradient: {...draft.backgroundGradient, to: c}})} />
                    <div style={{marginTop: 8}}>
                      <FormField label="Angle (degrees)">
                        <DelayedTextField type="number"
                                          value={String(draft.backgroundGradient.angle)}
                                          placeholder="135"
                                          data-test-id="gradient-angle-input"
                                          onChange={(v) => update({
                                            backgroundGradient: {...draft.backgroundGradient, angle: Number(v)}
                                          })} />
                      </FormField>
                    </div>
                  </div>
                )}
              </div>
            )}

            {draft.backgroundType === "image" && (
                <div style={optionBodyStyle}>
                  {draft.backgroundImage && draft.backgroundImage.url ? (
                    <div>
                      <img src={draft.backgroundImage.url} alt="background preview"
                           style={{width: "100%", maxHeight: 120, objectFit: "cover", borderRadius: 4,
                                   border: `1px solid ${theme.palette.border.light}`}} />
                      <div style={{marginTop: 12}}>
                        <FormField label="Fit">
                          <Select size="small" fullWidth data-test-id="background-fit-select"
                                  value={draft.backgroundImage.fit}
                                  onChange={(e) => update({backgroundImage: {...draft.backgroundImage, fit: e.target.value}})}>
                            <MenuItem value="cover">Cover</MenuItem>
                            <MenuItem value="stretch">Stretch</MenuItem>
                            <MenuItem value="tile">Tile</MenuItem>
                          </Select>
                        </FormField>
                      </div>
                      <Button size="small" color="error" variant="text" data-test-id="background-image-remove"
                              style={{marginTop: 8}}
                              startIcon={<i className="bhi-delete" style={{fontSize: "0.9rem"}} />}
                              onClick={removeImage}>Remove</Button>
                    </div>
                  ) : (
                    <Dropzone multiple={false}
                              accept={{"image/png": [".png"], "image/jpeg": [".jpg", ".jpeg"], "image/gif": [".gif"]}}
                              onDrop={handleDrop} className="react-dropzone">
                      <div style={dropzoneInnerStyle(theme)}>
                        <FontAwesomeIcon icon={faCloudUploadAlt}
                                         style={{fontSize: 24, color: theme.palette.text.secondary, marginBottom: 6}} />
                        <div style={{fontSize: 12, fontWeight: 600, color: theme.palette.text.main}}>
                          {isUploading ? "Uploading…" : "Drop an image or click to upload"}
                        </div>
                        <div style={{fontSize: 11, color: theme.palette.text.secondary, marginTop: 2}}>
                          PNG, JPEG or GIF, up to 10MB
                        </div>
                      </div>
                    </Dropzone>
                  )}
                  {uploadError && (
                    <div data-test-id="background-image-error"
                         style={{color: theme.palette.error.main, fontSize: 12, marginTop: 8}}>{uploadError}</div>
                  )}
                </div>
              )}
          </div>

          <div style={sectionStyle}>
            <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8}}>
              <span style={labelStyle}>Panel background</span>
              <FormControlLabel
                labelPlacement="start"
                style={{margin: 0}}
                label={<span style={{fontSize: 13}}>Match to background</span>}
                control={<Checkbox size="small"
                                   inputProps={{"data-test-id": "panel-match-checkbox"}}
                                   checked={!!draft.panelMatch}
                                   onChange={(e) => update(e.target.checked
                                     ? {panelMatch: true}
                                     : {panelMatch: false, panelColor: panelColorValue})} />} />
            </div>
            <HexField value={panelColorValue}
                      placeholder="#2a2a3e"
                      testId="panel-color-hex-input"
                      theme={theme}
                      onChange={(c) => update({panelMatch: false, panelColor: c})} />
          </div>

          {isGamifiedDashboard && (
            <div style={sectionStyle} data-test-id="aspect-ratio-section">
              <div style={labelStyle}>Aspect Ratio</div>
              <div style={{display: "flex", gap: 12, flexWrap: "wrap"}}>
                {Object.entries(GAMIFIED_ASPECT_RATIOS).map(([value, ratio]) => (
                  <AspectRatioCard key={value} value={value} ratio={ratio}
                                   isSelected={draftAspectRatio === value}
                                   onSelect={() => handleAspectRatioSelect(value)} theme={theme} />
                ))}
              </div>
            </div>
          )}

          <div style={sectionStyle} data-test-id="zoom-section">
            <div style={labelStyle}>Zoom</div>
            <span style={{display: "block", padding: "0 10px"}}>
                <Slider
                  size="small"
                  value={draft.zoom ?? DEFAULT_ZOOM}
                  min={ZOOM_MIN}
                  max={ZOOM_MAX}
                  step={ZOOM_STEP}
                  marks
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `${v}%`}
                  getAriaValueText={(v) => `${v}%`}
                  // The visible "Zoom" heading is a plain div, so without this the slider
                  // reaches a screen reader with no name at all.
                  aria-label="Zoom"
                  componentsProps={{input: {"data-test-id": "dashboard-zoom-slider"}}}
                  onChange={(e, value) => update({zoom: value})} />
            </span>
          </div>
        </div>

        <div style={{display: "flex", justifyContent: "flex-end", gap: 8, padding: "10px 16px",
                     borderTop: "1px solid rgba(128,128,128,0.2)"}}>
          <Button size="small" variant="text" data-test-id="dashboard-settings-cancel"
                  onClick={onClose} sx={{height: 30}}>Cancel</Button>
          <Button size="small" variant="contained" data-test-id="dashboard-settings-apply"
                  onClick={() => onApply(draft, draftAspectRatio)} sx={{height: 30}}>Apply</Button>
        </div>
      </div>

      <Cube19Dialog
        open={!!pendingAspectRatio}
        title="Edit Aspect Ratio"
        maxWidth="xs"
        onClose={() => setPendingAspectRatio(null)}
        actions={[
          <Button key="cancel" size="small" variant="text"
                  onClick={() => setPendingAspectRatio(null)}>Cancel</Button>,
          <Button key="confirm" size="small" variant="contained" data-test-id="aspect-ratio-confirm"
                  onClick={() => { setDraftAspectRatio(pendingAspectRatio); setPendingAspectRatio(null); }}>Confirm</Button>
        ]}>
        <div>Some panels may not fit within the new aspect ratio and may need manual adjustment.</div>
      </Cube19Dialog>
    </Drawer>
  );
};

export default DashboardSettingsDrawer;
